import { SortDescriptor } from '@progress/kendo-data-query';
import Moment from 'moment'
import * as Redux from 'redux'
import * as App from '../../app'
import * as DragCell from '../../components/drag-cell'
import * as Form from '../../components/form'
import * as IsActive from '../../components/is_active'
import * as RasaInput from '../../components/rasa-input'
import * as Sortable from '../../components/sortable'
import * as Request from '../../request'
import * as Router from '../../router'
import * as Constants from './constants'
import * as SendEmailModal from './send-email-modal'
import * as SendEmailConstants from './send-email-modal/constants'
import * as Types from './types'
import * as Utils from './utils'

const emptyDataState: Types.ComponentState = {
  emailString: '',
  items: [],
  itemsToSort: [],
  selectedDate: Moment().format('YYYY-MM-DD'),
  validation: {invalid: true, message: ''},
}

const emptyViewState = {
  addMenu: false,
  rankModal: false,
  selectedCutoff: SendEmailConstants.CUTOFF_SELECT_ONE_WEEK,
  sendStatus: '',
  sortMenu: false,
}

type Action = Types.SelectDateAction &
              Types.SelectEmailAction &
              App.UrlParsedAction<{selectedDate: Types.SelectedDate}> &
              Request.SuccessAction &
              IsActive.StateChangeActionObject

export const sortReducer = (event: Sortable.ReducerEvent<Sortable.SortableObject>) =>
  (state: Types.ComponentState, action: any): any => {
  switch (action.type) {
    case Sortable.sortTableConstant(event.requestEvent):
    case Sortable.sortTableConstant(event.sortEvent):
      return {
        ...state,
        items: Sortable.sortState(state.items, action.options.value),
      }
    default:
      return state
  }
}

export const viewReducer = (state = emptyViewState, action: Action & IsActive.FilterActionObject) => {
  switch (action.type) {
    case Constants.TOGGLE_RANK_MODAL:
    case Request.successConstant(Constants.SAVE_REORDERED_LEAD_POST):
      return {
        ...state,
        rankModal: !state.rankModal,
      }
    case Constants.TOGGLE_SORT_MENU:
      return {
        ...state,
        sortMenu: !state.sortMenu,
      }
    case Constants.TOGGLE_ADD_MENU:
      return {
        ...state,
        addMenu: !state.addMenu,
      }
    case Request.successConstant(SendEmailConstants.SEND_EMAIL_FUTURE_DATE):
      return {
        ...state,
        sendStatus: action.payload.response.statusMessage,
      }
    case IsActive.filterStatusConstant(SendEmailConstants.CHANGE_CUTOFF_DATE):
        return {
          ...state,
          selectedCutoff: action.filter,
        }
    default:
      return state
  }
}

export const sortableDataReducer = (state: Types.ComponentState = emptyDataState, action: any) => {
  switch (action.type) {
    case Constants.OPEN_SORT_MODAL:
      return {
        ...state,
        itemsToSort: state.items.filter( (e: Types.CCLeadContent) => e.type.includes(action.sortType)
                                                            && !e.type.includes(Constants.SPONSORED)),
      }
    default:
      return state
  }
}

export const dataReducer = (state: Types.ComponentState = emptyDataState, action: Action) => {
  switch (action.type) {
    case Request.requestConstant(Constants.ADD_LEAD_POST):
    case Request.requestConstant(Constants.PUT_LEAD_TEXT):
    case Request.requestConstant(Constants.POST_LEAD_TEXT):
    case Request.requestConstant(Constants.ADD_PROMOTION):
    case Request.requestConstant(Constants.EDIT_PROMOTION):
    case Request.requestConstant(Constants.SAVE_REORDERED_LEAD_POST):
    case Router.ON_ENTER:
      return {
        ...state,
        items: [],
      }
    case App.URL_PARSED:
      return {
        ...state,
        selectedDate: action.params.selectedDate || state.selectedDate,
      }
    case Constants.CALENDAR_DATE_CHANGE:
      return {
        ...state,
        items: [],
        selectedDate: action.date,
      }
    case Request.successConstant(Constants.FETCH_CALENDAR_POSTS):
      return {
        ...state,
        items: state.items.concat(
          action.payload.response.map((e: Types.Post) => ({
            ...e,
            content: `${e.name}/${e.title}`,
            end_datetime: Moment(e.end_date),
            grid_updated: Moment(e.updated),
            preview_image_url: e.image_url,
            start_datetime: Moment(e.start_date),
            type: `${e.feature_type} ${e.category_name}`,
          })),
        ),
      }
    case Request.successConstant(Constants.FETCH_CALENDAR_LEAD_TEXT):
      return {
        ...state,
        items: state.items.concat(
          action.payload.response.map((e: Types.Post) => ({
            ...e,
            content: e.description,
            end_datetime: Moment(e.end_date),
            grid_updated: Moment(e.updated),
            start_datetime: Moment(e.start_date),
            type: Constants.LEAD_TEXT,
          })),
        ),
      };
    case Request.successConstant(Constants.FETCH_CALENDAR_PROMOTIONS):
      return {
        ...state,
        items: state.items.concat(
          action.payload.response.promotions.map((e: Types.Post) => ({
            ...e,
            content: `${e.title}/${e.description}`,
            end_datetime: Moment(e.end_date),
            grid_updated: Moment(e.updated),
            start_datetime: Moment(e.start_date),
            type: Constants.PROMOTION,
          })),
        ),
      }
    case Constants.UPDATE_SELECTED_DATE:
      return {
        ...state,
        selectedDate: action.date,
      }
    case Constants.UPDATE_CALENDAR_UPDATE_STATUS:
      return {
        ...state,
        items: Utils.update(state.items, action.change.props.dataIndex - 1),
      }
    case SendEmailModal.UPDATE_EMAIL_ADDRESS:
      const emailArray = RasaInput.splitString(action.emailAddress)
      const trimEmails = RasaInput.trimList(emailArray)
      return {
        ...state,
        emailString: action.emailAddress,
        validation: RasaInput.validateEmailList(trimEmails),
      }
    default:
      return state
  }
}

const leadPostFormReducer = (sourceReducer: any) =>
 (state: Form.State<any>, action: any ) => {
   const sourceState = sourceReducer(state, action)
   if (action.type === Request.successConstant(Constants.ADD_LEAD_POST)) {
     return {
       ...sourceState,
       fields: {
        ...sourceState.fields,
        // TODO: column name should be abstracted
        description: action.payload.response.description,
        image_url: action.payload.response.image_url,
        rank: action.payload.response.rank,
        site_name: action.payload.response.name,
        title: action.payload.response.title,
        url: action.payload.response.url,
      },
     }
   } else {
     return sourceState
   }
}

const sortReducerEvent: Sortable.ReducerEvent<Types.Post> = {
  defaultSort: {field: 'type', dir: 'desc'},
  requestEvent: Constants.CALENDAR_SORT,
}

export interface InitialSortandViewState {
  isLoading: boolean,
  selectedCutoff: IsActive.Filter
  sort: SortDescriptor[]
}

const initialState: InitialSortandViewState = {
  isLoading: false,
  selectedCutoff: SendEmailConstants.CUTOFF_SELECT_ONE_WEEK,
  sort: [],
}

const dragCellReducer = DragCell.createDragReducer(Constants.FETCH_LEAD_POSTS)
const myDragReducer = (state: any, action: any) => {
  switch (action.type) {
    case Constants.FETCH_LEAD_POSTS_DRAG_CELL_START:
    case Constants.FETCH_LEAD_POSTS_DRAG_CELL_REORDER:
    case Constants.FETCH_LEAD_POSTS_DRAG_CELL_END:
    return {
      ...state,
      itemsToSort: dragCellReducer(state.itemsToSort, action),
    }
    default:
      return state
  }
}

const reorderReducer = (state: any, action: any) => {
  switch (action.type) {
    case Request.requestConstant(Constants.FETCH_CALENDAR_POSTS):
    case Request.requestConstant(Constants.SAVE_REORDERED_LEAD_POST):
      return {
        ...state,
        isSaving: true,
      }
    case Request.successConstant(Constants.SAVE_REORDERED_LEAD_POST):
    case Request.successConstant(Constants.FETCH_CALENDAR_POSTS):
    case Request.errorConstant(Constants.SAVE_REORDERED_LEAD_POST):
      return {
        ...state,
        isDragged: false,
        isSaving: false,
      }
    default:
      return state
  }
}

export const reducer = Redux.combineReducers({
  data: Request.reducerChain([sortableDataReducer, myDragReducer, dataReducer, sortReducer(sortReducerEvent)]),
  form: leadPostFormReducer(Form.createReducer(Constants.FEATURED_POST_FORM)),
  modal:  Request.reducerChain([SendEmailModal.modalReducer, SendEmailModal.keepModalOpenReducer]),
  view: Request.reducerChain([DragCell.createViewReducer(Constants.FETCH_LEAD_POSTS), viewReducer,
    reorderReducer,
    IsActive.filterReducer(Sortable.viewReducer(sortReducerEvent), Constants.CALENDAR_SORT, initialState),
  ]),
})



// WEBPACK FOOTER //
// ./src/pages/content-calendar/reducer.tsx